import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ImagePlus, X, Loader2, Sparkles, ArrowLeft, ArrowRight, Trash2, Download, RefreshCw, Copy, FileWarning } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILES = 100;
const MAX_SIZE_MB = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXT = /\.(jpe?g|png|webp)$/i;

const STATUS_META = {
    pending: { emoji: '⏳', color: 'text-slate-500', label: 'Чака обработка' },
    uploaded: { emoji: '📤', color: 'text-cyan-600', label: 'Качен' },
    processing: { emoji: '⚙️', color: 'text-blue-500', label: 'Анализира се' },
    success: { emoji: '✅', color: 'text-green-600', label: 'Данните са попълнени' },
    partial: { emoji: '⚠️', color: 'text-amber-600', label: 'Частично попълнен' },
    needs_review: { emoji: '🔍', color: 'text-fuchsia-600', label: 'Нуждае се от проверка' },
    possible_duplicate: { emoji: '👯', color: 'text-orange-600', label: 'Възможен дубликат' },
    skipped: { emoji: '⏭️', color: 'text-slate-400', label: 'Пропуснат' },
    error: { emoji: '❌', color: 'text-red-600', label: 'Грешка' },
    deleted: { emoji: '🗑️', color: 'text-slate-400', label: 'Изтрит' },
};

let fileIdCounter = 0;

export default function BulkImportDialog({ open, onClose, token, onDone, onShowDuplicates }) {
    const [files, setFiles] = useState([]); // {id, file, url, status, error}
    const [runAi, setRunAi] = useState(true);
    const [phase, setPhase] = useState('select'); // select | uploading | processing | done
    const [uploadDone, setUploadDone] = useState(0);
    const [job, setJob] = useState(null);
    const [retrying, setRetrying] = useState(false);
    const pollRef = useRef(null);
    const filesRef = useRef([]);
    filesRef.current = files;

    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const stopPoll = () => { clearInterval(pollRef.current); pollRef.current = null; };

    const startPoll = useCallback((importId) => {
        stopPoll();
        pollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${API}/admin/bulk-import/${importId}`, authHeaders);
                setJob(res.data);
                if (res.data.status === 'done') {
                    stopPoll();
                    setPhase('done');
                    onDone?.();
                }
            } catch (e) { /* keep polling */ }
        }, 3000);
    }, [token]); // eslint-disable-line

    // On open: check for an interrupted/active job to resume
    useEffect(() => {
        if (!open) { stopPoll(); return; }
        (async () => {
            try {
                const res = await axios.get(`${API}/admin/bulk-import/active`, authHeaders);
                const active = res.data.job;
                if (active) {
                    setJob(active);
                    if (active.status === 'ai_processing') {
                        setPhase('processing');
                        startPoll(active.import_id);
                        toast.info('Възстановена е прекъсната Bulk Import операция');
                    } else if (active.status === 'uploading') {
                        setPhase('resume_upload');
                    }
                }
            } catch (e) { /* no active job */ }
        })();
        return stopPoll;
    }, [open]); // eslint-disable-line

    useEffect(() => () => { filesRef.current.forEach(f => URL.revokeObjectURL(f.url)); stopPoll(); }, []);

    const resetAll = () => {
        files.forEach(f => URL.revokeObjectURL(f.url));
        setFiles([]); setRunAi(true); setPhase('select'); setUploadDone(0); setJob(null);
        stopPoll();
    };

    const handleClose = () => {
        if (phase === 'uploading') { toast.warning('Изчакайте качването да приключи'); return; }
        resetAll();
        onClose();
    };

    const addFiles = (fileList) => {
        const arr = Array.from(fileList || []);
        const rejected = [];
        let valid = [];
        for (const f of arr) {
            const typeOk = ALLOWED_TYPES.includes(f.type) || ALLOWED_EXT.test(f.name);
            if (!typeOk) { rejected.push(`${f.name} — неподдържан формат (позволени: JPG, JPEG, PNG, WEBP)`); continue; }
            if (f.size > MAX_SIZE_MB * 1024 * 1024) { rejected.push(`${f.name} — над ${MAX_SIZE_MB}MB`); continue; }
            const dup = files.some(x => x.file.name === f.name && x.file.size === f.size)
                || valid.some(x => x.file.name === f.name && x.file.size === f.size);
            if (dup) { rejected.push(`${f.name} — вече е добавен`); continue; }
            valid.push({ id: `bf_${++fileIdCounter}`, file: f, url: URL.createObjectURL(f), status: 'pending', error: null });
        }
        if (rejected.length) toast.error(rejected.slice(0, 5).join('\n') + (rejected.length > 5 ? `\n… и още ${rejected.length - 5}` : ''));
        if (files.length + valid.length > MAX_FILES) {
            toast.error('Можете да качите максимум 100 снимки наведнъж.');
            valid.slice(MAX_FILES - files.length).forEach(v => URL.revokeObjectURL(v.url));
            valid = valid.slice(0, MAX_FILES - files.length);
        }
        if (valid.length) setFiles(prev => [...prev, ...valid]);
    };

    const removeFile = (id) => setFiles(prev => {
        const f = prev.find(x => x.id === id);
        if (f) URL.revokeObjectURL(f.url);
        return prev.filter(x => x.id !== id);
    });

    const moveFile = (idx, dir) => setFiles(prev => {
        const next = [...prev];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return prev;
        [next[idx], next[j]] = [next[j], next[idx]];
        return next;
    });

    const clearAll = () => { files.forEach(f => URL.revokeObjectURL(f.url)); setFiles([]); };

    const buildChunks = (list) => {
        const chunks = [];
        let current = [], size = 0;
        for (const e of list) {
            if (current.length >= 5 || size + e.file.size > 20 * 1024 * 1024) {
                if (current.length) chunks.push(current);
                current = []; size = 0;
            }
            current.push(e); size += e.file.size;
        }
        if (current.length) chunks.push(current);
        return chunks;
    };

    const startUpload = async () => {
        if (files.length === 0) { toast.error('Изберете поне 1 снимка'); return; }
        if (files.length > MAX_FILES) { toast.error('Можете да качите максимум 100 снимки наведнъж.'); return; }
        setPhase('uploading');
        setUploadDone(0);
        let importId;
        try {
            const startRes = await axios.post(`${API}/admin/bulk-import/start`, { total_files: files.length }, authHeaders);
            importId = startRes.data.import_id;
        } catch (e) {
            toast.error('Грешка при стартиране: ' + (e.response?.data?.detail || e.message));
            setPhase('select');
            return;
        }
        let done = 0;
        for (const chunk of buildChunks(files)) {
            setFiles(prev => prev.map(f => chunk.some(c => c.id === f.id) ? { ...f, status: 'uploading' } : f));
            try {
                const fd = new FormData();
                chunk.forEach(e => fd.append('files', e.file));
                const res = await axios.post(`${API}/admin/bulk-upload-drafts?import_id=${importId}`, fd, { ...authHeaders, timeout: 180000 });
                const errByName = {};
                (res.data.errors || []).forEach(er => { errByName[er.filename] = er.error; });
                setFiles(prev => prev.map(f => {
                    if (!chunk.some(c => c.id === f.id)) return f;
                    const err = errByName[f.file.name];
                    return err ? { ...f, status: 'error', error: err } : { ...f, status: 'uploaded' };
                }));
            } catch (e) {
                const msg = e.response?.data?.detail || e.message;
                setFiles(prev => prev.map(f => chunk.some(c => c.id === f.id) ? { ...f, status: 'error', error: msg } : f));
            }
            done += chunk.length;
            setUploadDone(done);
        }
        try {
            const fin = await axios.post(`${API}/admin/bulk-import/${importId}/finish`, { run_ai: runAi }, authHeaders);
            if (fin.data.ai_started) {
                setPhase('processing');
                startPoll(importId);
            } else {
                if (runAi && fin.data.message) toast.warning(fin.data.message);
                const res = await axios.get(`${API}/admin/bulk-import/${importId}`, authHeaders);
                setJob(res.data);
                setPhase('done');
                onDone?.();
            }
        } catch (e) {
            toast.error('Грешка при приключване: ' + (e.response?.data?.detail || e.message));
            setPhase('done');
        }
    };

    const retryFailedAi = async () => {
        const failedIds = (job?.products || []).filter(p => ['error', 'needs_review'].includes(p.status)).map(p => p.product_id);
        if (!failedIds.length) { toast.info('Няма продукти за повторен опит'); return; }
        setRetrying(true);
        try {
            await axios.post(`${API}/admin/ai-fill-drafts`, { product_ids: failedIds }, authHeaders);
            toast.success(`Повторен AI опит за ${failedIds.length} продукта`);
            await axios.post(`${API}/admin/bulk-import/${job.import_id}/finish`, { run_ai: false }, authHeaders).catch(() => {});
            setPhase('processing');
            startPoll(job.import_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при повторния опит');
        } finally {
            setRetrying(false);
        }
    };

    const downloadReport = () => {
        const rows = [['product_id', 'filename', 'name', 'status', 'message']];
        (job?.products || []).forEach(p => rows.push([p.product_id, p.filename || '', (p.name || '').replace(/"/g, "'"), p.status, (p.message || '').replace(/"/g, "'")]));
        (job?.upload_errors || []).forEach(e => rows.push(['-', e.filename || '', '', 'upload_error', e.error || '']));
        const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `bulk-import-${job?.import_id || 'report'}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const summary = job?.summary || {};
    const dupCount = summary.possible_duplicate || 0;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
                <DialogHeader>
                    <DialogTitle className="dark:text-white flex items-center gap-2">
                        <ImagePlus className="h-5 w-5 text-cyan-500" />
                        Bulk Import Снимки → Чернови
                    </DialogTitle>
                    <p className="text-sm text-slate-500 dark:text-slate-400 pt-1">
                        Качете до {MAX_FILES} снимки наведнъж. За всяка се създава чернова, AI попълва данните и системата проверява за дубликати.
                    </p>
                </DialogHeader>

                {/* ===== RESUME ABANDONED UPLOAD ===== */}
                {phase === 'resume_upload' && job && (
                    <div className="space-y-3 py-2" data-testid="bulk-import-resume">
                        <div className="p-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-200">
                            ⚠️ Има прекъсната Bulk Import операция ({job.uploaded_count} качени снимки). Можете да стартирате AI за вече качените или да затворите задачата.
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" className="dark:border-slate-600" data-testid="bulk-import-abandon-btn"
                                onClick={async () => {
                                    await axios.post(`${API}/admin/bulk-import/${job.import_id}/close`, {}, authHeaders).catch(() => {});
                                    resetAll();
                                }}>
                                Затвори задачата
                            </Button>
                            <Button className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="bulk-import-resume-ai-btn"
                                onClick={async () => {
                                    try {
                                        const fin = await axios.post(`${API}/admin/bulk-import/${job.import_id}/finish`, { run_ai: true }, authHeaders);
                                        if (fin.data.ai_started) { setPhase('processing'); startPoll(job.import_id); }
                                        else { toast.warning(fin.data.message); setPhase('done'); }
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Грешка'); }
                                }}>
                                <Sparkles className="h-4 w-4 mr-2" /> Стартирай AI за качените
                            </Button>
                        </div>
                    </div>
                )}

                {/* ===== SELECT / UPLOAD PHASE ===== */}
                {(phase === 'select' || phase === 'uploading') && (
                    <div className="space-y-4 py-2">
                        {phase === 'select' && (
                            <label
                                htmlFor="bulk-import-file-input"
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); addFiles(e.dataTransfer.files); }}
                                className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg py-8 px-4 cursor-pointer transition-colors border-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/10"
                                data-testid="bulk-import-dropzone"
                            >
                                <ImagePlus className="h-10 w-10 text-cyan-500 mb-2" />
                                <p className="text-sm font-medium dark:text-white">Пуснете снимките тук или кликнете за избор</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">JPG, JPEG, PNG, WEBP · до {MAX_FILES} файла · макс. {MAX_SIZE_MB}MB на файл</p>
                                <input id="bulk-import-file-input" type="file" multiple accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                    className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="bulk-import-file-input" />
                            </label>
                        )}

                        {files.length > 0 && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium dark:text-white" data-testid="bulk-import-counter">
                                        Избрани {files.length} от {MAX_FILES} снимки
                                    </p>
                                    {phase === 'select' && (
                                        <Button type="button" variant="ghost" size="sm" onClick={clearAll}
                                            className="text-red-500 hover:text-red-600 h-7 px-2" data-testid="bulk-import-clear-btn">
                                            <Trash2 className="h-3 w-3 mr-1" /> Премахни всички
                                        </Button>
                                    )}
                                </div>
                                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 max-h-56 overflow-y-auto p-2 border rounded dark:border-slate-700">
                                    {files.map((f, idx) => (
                                        <div key={f.id} className="relative group" data-testid={`bulk-import-preview-${idx}`}>
                                            <img src={f.url} alt={f.file.name} title={f.file.name}
                                                className={`w-full h-16 object-cover rounded border dark:border-slate-600 ${f.status === 'error' ? 'ring-2 ring-red-500' : f.status === 'uploaded' ? 'ring-2 ring-green-500' : f.status === 'uploading' ? 'opacity-60 animate-pulse' : ''}`} />
                                            <span className="absolute bottom-0 left-0 bg-black/60 text-white text-[9px] px-1 rounded-tr">{idx + 1}</span>
                                            {phase === 'select' && (
                                                <>
                                                    <button type="button" onClick={() => removeFile(f.id)} aria-label="Премахни"
                                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        data-testid={`bulk-import-remove-${idx}`}>
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                    <div className="absolute bottom-0 right-0 flex opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button type="button" onClick={() => moveFile(idx, -1)} className="bg-black/60 text-white p-0.5" aria-label="Наляво"><ArrowLeft className="h-3 w-3" /></button>
                                                        <button type="button" onClick={() => moveFile(idx, 1)} className="bg-black/60 text-white p-0.5" aria-label="Надясно"><ArrowRight className="h-3 w-3" /></button>
                                                    </div>
                                                </>
                                            )}
                                            {f.status === 'error' && <span title={f.error} className="absolute top-0 left-0 bg-red-600 text-white text-[9px] px-1 rounded-br">!</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <label className="flex items-center gap-2 p-3 rounded border dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/40">
                            <input type="checkbox" checked={runAi} disabled={phase === 'uploading'}
                                onChange={(e) => setRunAi(e.target.checked)} className="h-4 w-4 accent-purple-500" data-testid="bulk-import-ai-checkbox" />
                            <Sparkles className="h-4 w-4 text-purple-500" />
                            <span className="text-sm dark:text-white">Пусни AI попълване и проверка за дубликати след качване</span>
                        </label>

                        {phase === 'uploading' && (
                            <div className="space-y-2" data-testid="bulk-import-progress">
                                <div className="flex items-center justify-between text-sm dark:text-white">
                                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-cyan-500" /> Качване на групи...</span>
                                    <span>Качени {uploadDone} от {files.length}</span>
                                </div>
                                <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                    <div className="h-full bg-cyan-500 transition-all" style={{ width: `${files.length ? (uploadDone / files.length) * 100 : 0}%` }} />
                                </div>
                            </div>
                        )}

                        <div className="flex gap-2 justify-end pt-2">
                            <Button variant="outline" onClick={handleClose} disabled={phase === 'uploading'} className="dark:border-slate-600" data-testid="bulk-import-close-btn">Отказ</Button>
                            <Button onClick={startUpload} disabled={phase === 'uploading' || files.length === 0}
                                className="btn-primary bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="bulk-import-submit-btn">
                                {phase === 'uploading' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Качване...</> : <><ImagePlus className="h-4 w-4 mr-2" /> Качи {files.length || ''} снимки</>}
                            </Button>
                        </div>
                    </div>
                )}

                {/* ===== AI PROCESSING / DONE PHASE ===== */}
                {(phase === 'processing' || phase === 'done') && job && (
                    <div className="space-y-3 py-2">
                        {phase === 'processing' && (
                            <div className="space-y-2" data-testid="bulk-import-ai-progress">
                                <div className="flex items-center justify-between text-sm dark:text-white">
                                    <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-purple-500 animate-pulse" /> AI обработва продуктите...</span>
                                    <span>Обработени {job.ai_done} от {job.ai_total}</span>
                                </div>
                                <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                    <div className="h-full bg-purple-500 transition-all" style={{ width: `${job.ai_total ? (job.ai_done / job.ai_total) * 100 : 0}%` }} />
                                </div>
                            </div>
                        )}

                        {phase === 'done' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="bulk-import-summary">
                                <div className="p-2 rounded bg-slate-100 dark:bg-slate-700/60 dark:text-white">📤 Качени: <strong>{summary.uploaded || 0}</strong></div>
                                <div className="p-2 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">✅ Попълнени: <strong>{summary.success || 0}</strong></div>
                                <div className="p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">⚠️ Частично: <strong>{summary.partial || 0}</strong></div>
                                <div className="p-2 rounded bg-fuchsia-50 dark:bg-fuchsia-900/20 text-fuchsia-700 dark:text-fuchsia-300">🔍 За проверка: <strong>{summary.needs_review || 0}</strong></div>
                                <div className="p-2 rounded bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300">👯 Дубликати: <strong>{dupCount}</strong></div>
                                <div className="p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">❌ Грешки: <strong>{(summary.error || 0) + (summary.upload_errors || 0)}</strong></div>
                                <div className="p-2 rounded bg-slate-100 dark:bg-slate-700/60 dark:text-white">⏳ Чакащи: <strong>{summary.pending || 0}</strong></div>
                                <div className="p-2 rounded bg-slate-100 dark:bg-slate-700/60 dark:text-white">📝 Чернови: <strong>{summary.uploaded || 0}</strong></div>
                            </div>
                        )}

                        <div className="max-h-64 overflow-y-auto border rounded dark:border-slate-700 divide-y dark:divide-slate-700" data-testid="bulk-import-product-list">
                            {(job.products || []).map((p) => {
                                const meta = STATUS_META[p.status] || STATUS_META.pending;
                                return (
                                    <div key={p.product_id} className="flex items-start gap-2 p-2 text-sm" data-testid={`bulk-import-item-${p.product_id}`}>
                                        <span className={`${meta.color} shrink-0`}>{meta.emoji}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium dark:text-white truncate">{p.name || p.filename}</div>
                                            <div className={`text-xs ${meta.color}`}>{meta.label}{p.message ? `: ${p.message}` : ''}</div>
                                        </div>
                                    </div>
                                );
                            })}
                            {(job.upload_errors || []).map((e, i) => (
                                <div key={`ue-${i}`} className="flex items-start gap-2 p-2 text-sm">
                                    <FileWarning className="h-4 w-4 text-red-500 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium dark:text-white truncate">{e.filename}</div>
                                        <div className="text-xs text-red-500">Грешка при качване: {e.error}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {phase === 'done' && (
                            <div className="flex flex-wrap gap-2 justify-end pt-2">
                                <Button variant="outline" size="sm" onClick={downloadReport} className="dark:border-slate-600" data-testid="bulk-import-download-report-btn">
                                    <Download className="h-4 w-4 mr-1" /> Изтегли отчет
                                </Button>
                                {(summary.error > 0 || summary.needs_review > 0) && (
                                    <Button variant="outline" size="sm" onClick={retryFailedAi} disabled={retrying}
                                        className="border-purple-500 text-purple-600 dark:text-purple-400" data-testid="bulk-import-retry-failed-btn">
                                        {retrying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Опитай отново неуспешните
                                    </Button>
                                )}
                                {dupCount > 0 && (
                                    <Button size="sm" onClick={() => { handleClose(); onShowDuplicates?.(); }}
                                        className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="bulk-import-view-duplicates-btn">
                                        <Copy className="h-4 w-4 mr-1" /> Възможни дубликати ({dupCount})
                                    </Button>
                                )}
                                <Button size="sm" onClick={() => { onDone?.(); handleClose(); }}
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="bulk-import-view-drafts-btn">
                                    Прегледай черновите
                                </Button>
                            </div>
                        )}
                        {phase === 'processing' && (
                            <div className="flex justify-end gap-2 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={async () => {
                                        try {
                                            await axios.post(`${API}/admin/ai-fill-drafts/stop`, {}, authHeaders).catch(() => {});
                                            await axios.post(`${API}/admin/bulk-import/${job.import_id}/close`, {}, authHeaders);
                                            toast.success('Задачата е затворена — можете да стартирате нов импорт');
                                            resetAll();
                                            onDone?.();
                                        } catch (e) {
                                            toast.error('Грешка при затваряне на задачата');
                                        }
                                    }}
                                    className="border-red-300 text-red-600 dark:text-red-400 dark:border-red-800"
                                    data-testid="bulk-import-force-close-btn"
                                >
                                    Прекрати и затвори задачата
                                </Button>
                                <Button variant="outline" onClick={handleClose} className="dark:border-slate-600" data-testid="bulk-import-bg-close-btn">
                                    Продължи на заден фон
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
